import { useMemo, useState } from "react";
import { toast } from "react-toastify";

import Button from "components/v1/Button";
import DetailTabArchived from "components/molecules/buyer-match/detail-tab-archived";
import ListListingDetailBuyerMatch from "components/molecules/buyer-match/list-listing-detail-buyer-match";
import ModalDetailCustomer from "components/molecules/buyer-match/modal-detail-customer";
import ModalKonfirmasi from "components/molecules/buyer-match/modal-konfirmasi";
import ModalLoader from "components/organism/ModalLoader";
import Tooltip from "components/atomic/shared/Tooltip";

// Assets
import IArrowShortail from "assets/i-arrow-shortail.js";
import IInfo from "assets/i-info.js";
import IMagnifier from "assets/i-magnifier.js";
import IRefresh from "assets/i-refresh.js";
import ITrash from "assets/i-trash.js";

// Ctx
import { useBuyerMatchCtx } from "contexts/buyer-match";

// Api
import {
  useGetDetailCustomer,
  useGetListListingCustomerInfinite,
} from "api/buyer-match/query";
import {
  useDeleteCustomer,
  useUpdateStatusCustomer,
} from "api/buyer-match/mutation";
import { useDebounce } from "hooks/useDebounce";

const DetailBuyerArchived = () => {
  const {
    selectedLeadId,
    setSelectedLeadId,
    setIsOpenDetailBuyer,
    listCustomerActive,
    countListingBuyerMatch,
    refetchListCustomer
  } = useBuyerMatchCtx();

  const [isOpenModal, setIsOpenModal] = useState(""); // "confirm-restore" | "confirm-delete"
  const [search, setSearch] = useState("");
  const [listCustomerParams, setListCustomerParams] = useState({
    q: "",
    status: "interested", // matched, sent, interested, not_interested,
    page: 1,
    _limit: 10,
  });

  const { mutate: deleteCustomerMutation } = useDeleteCustomer();
  const { mutate: updateStatusCustomerMutation } = useUpdateStatusCustomer();

  const onCloseModal = () => {
    setIsOpenModal("");
  };

  const {
    data: detailCustomer,
    refetch: refetchDetailCustomer,
    isLoading: isLoadingDetailCustomer,
  } = useGetDetailCustomer({
    id: selectedLeadId,
    enabled: !!selectedLeadId,
  });

  const {
    data: listListingCustomer,
    refetch: refetchListListingCustomer,
    isLoading: isLoadinigListListing,
    fetchNextPage,
    hasNextPage,
  } = useGetListListingCustomerInfinite({
    id: selectedLeadId,
    params: listCustomerParams,
    enabled: !!selectedLeadId,
  });

  const formatDetailCustomer = useMemo(() => {
    return detailCustomer?.data?.data ?? [];
  }, [detailCustomer]);

  const formatListListingCustomer = useMemo(() => {
    if (!listListingCustomer?.pages?.length) return [];

    return listListingCustomer.pages.flatMap((page) => {
      return page?.data?.data?.listings ?? [];
    });
  }, [listListingCustomer]);

  const handleMoveTo = (status) => {
    setListCustomerParams({
      ...listCustomerParams,
      status,
    });
  };

  const { debounceFn } = useDebounce({
    onChange: (query) => {
      setListCustomerParams({
        ...listCustomerParams,
        q: query,
      });
    },
    delay: 500,
  });

  const handleToListBuyer = () => {
    setSelectedLeadId(null);
    setIsOpenDetailBuyer(false);
  };

  const onSubmitRestoreBuyerAgent = () => {
    const data = {
      customer: {
        is_archive: false,
      },
    };
    updateStatusCustomerMutation(
      { customerId: formatDetailCustomer?.id, data },
      {
        onSuccess: () => {
          toast.success("Profil Customer berhasil dipulihkan");
          refetchDetailCustomer();
          refetchListCustomer();
          handleToListBuyer();
        },
        onError: (error) => {
          toast.error(
            error?.response?.data?.message ?? "Gagal memulihkan profil customer"
          );
        },
        onSettled: () => {
          onCloseModal();
        },
      }
    );
  };

  const onSubmitDeleteBuyerAgent = () => {
    deleteCustomerMutation(
      { customerId: formatDetailCustomer?.id },
      {
        onSuccess: () => {
          toast.success("Profil Customer berhasil dihapus");
          refetchListCustomer();
          handleToListBuyer();
        },
        onError: (error) => {
          toast.error(
            error?.response?.data?.message ?? "Gagal menghapus profil customer"
          );
        },
        onSettled: () => {
          onCloseModal();
        },
      }
    );
  };

  return (
    <>
      <div className="min-h-[calc(100vh-81px-73px-48px)] rounded-[4px] bg-white">
        <div className="px-4 py-2 flex items-center justify-between mb-2 border-b-[0.5px] border-solid border-[#D8D8D9]">
          <div className="flex items-center justify-between gap-[12px]">
            <Button
              icon={IArrowShortail}
              type="secondary"
              iconColor="#252526"
              onClick={handleToListBuyer}
            />
            <span className="text-[13px] leading-[21px] tracking-[-0.4px] font-inter-semibold text-[#252526]">
              {formatDetailCustomer?.name}
            </span>
            <div className="w-[0.5px] h-[22px] bg-[#D8D8D9]" />
            <span className="text-[13px] leading-[21px] tracking-[-0.4px] font-inter text-[#808285]">
              {formatDetailCustomer?.whatsapp_number}
            </span>
          </div>

          <div className="flex items-center gap-1">
            <Tooltip content="Detail Customer">
              <Button
                icon={IInfo}
                type="secondary"
                iconColor="#252526"
                className="!p-[10px]"
                onClick={() => setIsOpenModal("detail")}
              />
            </Tooltip>
            <Tooltip content="Pulihkan Customer">
              <Button
                icon={IRefresh}
                type="secondary"
                iconColor="#252526"
                className="!p-[10px]"
                disabled={listCustomerActive?.length >= 10}
                onClick={() => setIsOpenModal("confirm-restore")}
              />
            </Tooltip>
            <Tooltip content="Hapus Customer">
              <Button
                icon={ITrash}
                type="negative"
                iconColor="#FD4949"
                className="!p-[10px]"
                onClick={() => setIsOpenModal("confirm-delete")}
              />
            </Tooltip>
          </div>
        </div>

        <div className="px-4 py-2 flex items-center justify-between">
          <DetailTabArchived
            handleMoveTo={handleMoveTo}
            status={listCustomerParams.status}
            countListing={countListingBuyerMatch}
          />
          <div className="h-[32px] p-2 flex justify-center items-center gap-1 rounded-[4px] border-[1px] border-solid border-[#D8D8D9]">
            <IMagnifier />
            <input
              className="h-[24px] py-2 text-[11px] leading-[17px] tracking-[-0.4px] font-inter text-[#252526] placeholder:text-[#808285]"
              placeholder="Cari listing dengan judul"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                debounceFn(e.target.value);
              }}
            />
          </div>
        </div>

        <div className="px-4 py-2 overflow-x-auto">
          <ListListingDetailBuyerMatch
            detailCustomer={formatDetailCustomer}
            listListingCustomer={formatListListingCustomer}
            status={listCustomerParams.status}
            refetchListListingCustomer={refetchListListingCustomer}
            listCustomerParams={listCustomerParams}
            setListCustomerParams={setListCustomerParams}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isSearch={!!search}
          />
        </div>
      </div>

      <ModalDetailCustomer
        isOpen={isOpenModal === "detail"}
        onClose={onCloseModal}
        detailCustomer={formatDetailCustomer}
      />

      <ModalKonfirmasi
        isOpen={isOpenModal === "confirm-restore"}
        onClose={onCloseModal}
        title="Pulihkan Customer?"
        description="Profil customer ini akan dipulihkan. Anda dapat menemukannya di daftar customer."
        leftBtnText="Batal"
        rightBtnText="Pulihkan"
        leftBtnAction={onCloseModal}
        rightBtnAction={onSubmitRestoreBuyerAgent}
      />

      <ModalKonfirmasi
        isOpen={isOpenModal === "confirm-delete"}
        onClose={onCloseModal}
        title="Hapus Permanen Customer?"
        description="Seluruh informasi pada profil customer ini akan dihapus secara permanen."
        leftBtnText="Batal"
        rightBtnText="Hapus Permanen"
        rightBtnVariant="negative"
        leftBtnAction={onCloseModal}
        rightBtnAction={onSubmitDeleteBuyerAgent}
        withCounter
        maxCountDown={10}
      />

      <ModalLoader open={isLoadingDetailCustomer || isLoadinigListListing} />
    </>
  );
};

export default DetailBuyerArchived;
